import React, { useState } from 'react';
import {
  Table, Badge, Spinner, Button,
} from 'react-bootstrap';
import { DocumentStatus } from '../../../Components/Billingo/InvoiceStatusPill';
import DocumentTotal from '../../../Components/Billingo/DocumentTotal';
import StaleApiNotice from '../../../Components/Billingo/StaleApiNotice';
import ExpandableBookingsCell from '../../../Components/Billingo/ExpandableBookingsCell';
import BookingContextList from '../../../Components/Billingo/BookingContextList';
import InvoiceFilters, { countActiveFilters, EMPTY_FILTERS } from './InvoiceFilters';
import ListPager from '../../../Components/ListPager';
import useDocumentActions from '../../../Components/Billingo/useDocumentActions';
import { DOC_TYPE_META, docTypeLabel } from '../../../Utils/billingoDocuments';

// One page of Billingo documents. Presentational — the page owns the fetch,
// the filters and the pager, so filtering spans the whole collection rather
// than the rows that happen to be loaded.
function AllInvoicesList({
  documents = [],
  loading = false,
  onChanged = () => {},
  filters = EMPTY_FILTERS,
  onFiltersChange = () => {},
  pagination = null,
  onPageChange = () => {},
  onLimitChange = () => {},
  facets = { created_by: [], currencies: [] },
  paginated = true,
}) {
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [expanded, setExpanded] = useState(null);
  // Each document's own bookings say whether its Számla is blocked and whom
  // to send it to (useDocumentActions).
  const {
    actionsFor, openDetails, alert, modals,
  } = useDocumentActions({ onChanged });

  const stale = documents.some((doc) => !('booking_ids' in doc));
  // The API filters and pages; the rows handed in are already the answer.
  const filtered = documents;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
        <h6 className="mb-0">
          All invoices
          {pagination && pagination.total > 0 && (
            <span className="text-muted fw-normal">{` · ${pagination.total}`}</span>
          )}
        </h6>
      </div>

      <StaleApiNotice
        show={stale}
        detail="the order &amp; trip context and the issuer of each document"
      />
      <StaleApiNotice
        show={!paginated}
        detail="server-side paging and filtering (every document is being loaded at once)"
      />

      <InvoiceFilters
        filters={filters}
        onChange={onFiltersChange}
        facets={facets}
        open={filtersOpen}
        onToggle={() => setFiltersOpen((o) => !o)}
      />

      {alert}

      {loading && (
        <div className="text-muted small">
          <Spinner size="sm" animation="border" className="me-2" />
          Loading…
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <div className="text-center text-muted py-5">
          <i className="fa fa-file-invoice fa-2x mb-2 d-block opacity-50" />
          {countActiveFilters(filters) > 0 || filters.q
            ? 'No documents match your filters.'
            : 'No documents issued yet. Create one to get started.'}
        </div>
      )}

      {filtered.length > 0 && (
        <Table responsive hover className="align-middle responsive-card-table">
          <thead className="table-light">
            <tr>
              <th>Type</th>
              <th>Number</th>
              <th>Order &amp; trip</th>
              <th>Issued by</th>
              <th className="text-end">Total</th>
              <th>Status</th>
              <th className="text-end" aria-label="actions" />
            </tr>
          </thead>
          <tbody>
            {filtered.map((doc) => {
              const isOpen = expanded === doc.id;
              const bookings = doc.bookings || [];
              return (
                <React.Fragment key={doc.id}>
                  <tr>
                    <td data-label="Type">
                      <Badge
                        bg={DOC_TYPE_META[doc.type]?.variant || 'secondary'}
                        text={DOC_TYPE_META[doc.type]?.variant === 'light' ? 'dark' : undefined}
                      >
                        {docTypeLabel(doc.type)}
                      </Badge>
                    </td>
                    <td data-label="Number">
                      {/* The number opens the document — its info, the order(s)
                        it bills and its lines. */}
                      <Button
                        variant="link"
                        className="p-0 fw-medium text-decoration-none"
                        onClick={() => openDetails(doc)}
                      >
                        {doc.number}
                      </Button>
                    </td>
                    <td data-label="Order &amp; trip" style={{ maxWidth: 280 }}>
                      <ExpandableBookingsCell
                        bookings={bookings}
                        fallbackRefs={doc.order_references}
                        expanded={isOpen}
                        onToggle={() => setExpanded(isOpen ? null : doc.id)}
                      />
                    </td>
                    <td data-label="Issued by" className="small text-muted text-truncate" style={{ maxWidth: 150 }} title={doc.created_by || ''}>
                      {doc.created_by || '—'}
                    </td>
                    <td data-label="Total" className="text-end"><DocumentTotal doc={doc} /></td>
                    <td data-label="Status"><DocumentStatus doc={doc} /></td>
                    <td className="text-end text-nowrap">
                      {actionsFor(doc)}
                    </td>
                  </tr>
                  {isOpen && (
                  <tr>
                    <td colSpan={7} className="bg-body-tertiary">
                      <BookingContextList bookings={bookings} />
                    </td>
                  </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </Table>
      )}

      {pagination && (
        <ListPager
          pagination={pagination}
          onPageChange={onPageChange}
          onLimitChange={onLimitChange}
        />
      )}

      {modals}
    </div>
  );
}

export default AllInvoicesList;
